/**
 * The settings frame — the sidebar Epic 4's five screens share.
 *
 * Visual Reference: `settings-members.html`, whose chrome is identical on
 * `settings-profile`, `settings-workspaces`, `settings-billing`,
 * `settings-credentials` and `settings-audit`.
 *
 * Read off the screen: the column is `--mdx-sidebar-width`, white, `16px 12px`,
 * items on `--mdx-space-2`. The back link is 13px 600 `--mdx-brand-deep` with
 * `6px 12px 16px`; the eyebrow is the system's 12px/700/`.1em` with `0 12px
 * 8px`; items are the sidebar spec — `9px 12px`, radius 10px, active
 * `--mdx-brand-tint` behind `--mdx-brand-deep` at 600.
 *
 * TWO WIDTHS, ONE COMPONENT (ARCH §7.5). At 1440px it is a permanent column
 * beside the content. At 375px the mockup drops the column entirely and puts a
 * full-width `select` at the top of the content pane — not the main shell's
 * sheet, because this is a section switch rather than the product's navigation.
 * That is the screen's own answer and it is taken as drawn.
 *
 * It replaces the MAIN sidebar rather than nesting inside it: the screen shows
 * no Dashboard/Leads/Funnels column, and "← Back to Leadgible" is what leaves.
 *
 * Story 4.1.
 */
import type { ReactNode } from 'react'
import { Select } from '../ui/Select'
import { BACK_PATH, SETTINGS_NAV } from './settings-nav'

export function SettingsLayout({ path, children }: { path: string; children: ReactNode }) {
  return (
    <div className="flex min-h-0 flex-1 items-stretch">
      <nav
        aria-label="Settings"
        className="hidden w-mdx-sidebar shrink-0 flex-col gap-mdx-2 border-r border-mdx-line bg-mdx-white px-mdx-12 py-mdx-16 lg:flex"
      >
        <a
          href={BACK_PATH}
          className="px-mdx-12 pb-mdx-16 pt-mdx-6 text-mdx-13 font-mdx-semibold text-mdx-brand-deep no-underline"
        >
          ← Back to Leadgible
        </a>
        <span className="px-mdx-12 pb-mdx-8 text-mdx-12 font-mdx-bold uppercase tracking-mdx-eyebrow text-mdx-slate">
          Settings
        </span>
        {SETTINGS_NAV.map((item) => {
          const active = path === item.path
          return (
            <a
              key={item.path}
              href={item.path}
              aria-current={active ? 'page' : undefined}
              className={[
                'flex items-center gap-mdx-10 rounded-mdx-control px-mdx-12 py-mdx-9 text-mdx-15 no-underline',
                'transition-colors duration-mdx-swift ease-mdx-swift',
                active
                  ? 'bg-mdx-brand-tint font-mdx-semibold text-mdx-brand-deep'
                  : 'font-mdx-medium text-mdx-ink hover:bg-mdx-ground',
              ].join(' ')}
            >
              {item.label}
            </a>
          )
        })}
      </nav>

      <main className="flex min-w-0 flex-1 flex-col gap-mdx-20 px-mdx-24 pb-mdx-48 pt-mdx-32">
        {/* 375px: the column becomes a section switcher above the heading. */}
        <Select
          label="Settings section"
          wide
          value={path}
          onChange={(event) => window.location.assign(event.target.value)}
          className="lg:hidden"
        >
          {/* The current section first, as the mockup renders it. */}
          {[...SETTINGS_NAV].sort((a) => (a.path === path ? -1 : 0)).map((item) => (
            <option key={item.path} value={item.path}>
              {item.label}
            </option>
          ))}
        </Select>
        {children}
      </main>
    </div>
  )
}
